import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight, LuCircleCheck, LuFlame } from 'react-icons/lu';
import { ButtonLink } from '../../ui/Button';
import { DifficultyLabel } from '../../ui/Labels';
import { Skeleton } from '../../ui/Feedback';
import { useStrings } from '../../tenant/TenantProvider';
import { useCatalog } from '../../stores/catalog';
import { useNow } from '../../hooks/useNow';
import { acceptanceRate, formatCountdown, formatDayUtc } from '../../lib/format';
import { cx } from '../../ui/cx';

function nextUtcMidnight(todayIso) {
  const [year, month, day] = todayIso.split('-').map(Number);
  return Date.UTC(year, month - 1, day + 1);
}

function ResetCountdown({ todayIso }) {
  const t = useStrings();
  const now = useNow(1000);
  const remaining = nextUtcMidnight(todayIso) - now;
  const expired = remaining <= 0;

  useEffect(() => {
    if (expired) useCatalog.getState().loadDaily();
  }, [expired]);

  return <span className="today-hero__reset mono">{t('problems.today.resetsIn', { time: formatCountdown(remaining) })}</span>;
}

function StreakNote({ streak, solvedToday }) {
  const t = useStrings();
  if (solvedToday) {
    return (
      <p className="today-hero__note today-hero__note--done">
        <LuCircleCheck aria-hidden="true" />
        {t('problems.today.streakDone', { count: streak })}
      </p>
    );
  }
  return (
    <p className="today-hero__note">
      <LuFlame aria-hidden="true" />
      {streak > 0 ? t('problems.today.streakKeep', { count: streak }) : t('problems.today.streakStart')}
    </p>
  );
}

export function TodayHero({ daily, problem, suggestion, primary, continueProblem }) {
  const t = useStrings();

  if (!daily) {
    return (
      <section className="panel today-hero" aria-busy="true">
        <Skeleton width="30%" />
        <Skeleton width="60%" height={40} style={{ marginTop: 28 }} />
        <Skeleton width="40%" style={{ marginTop: 16 }} />
        <Skeleton width={160} height={44} style={{ marginTop: 32 }} />
      </section>
    );
  }

  const today = daily.today;
  const solvedToday = Boolean(today && daily.calendar.some((entry) => entry.date === daily.todayIso && entry.solved));
  const focus = today
    ? {
        id: today.questionId,
        title: today.title,
        difficulty: today.difficulty,
        rate: acceptanceRate(today.acceptedCount, today.submissionCount),
        tags: problem?.tags ?? [],
      }
    : suggestion
      ? {
          id: suggestion.id,
          title: suggestion.title,
          difficulty: suggestion.difficulty,
          rate: acceptanceRate(suggestion.acceptedCount, suggestion.submissionCount),
          tags: suggestion.tags,
        }
      : null;

  return (
    <section
      className={cx('panel today-hero lit', focus && !solvedToday && 'energy energy--glow', solvedToday && 'is-solved')}
      aria-labelledby="today-hero-title"
    >
      {focus && (
        <span className="today-hero__art" aria-hidden="true">
          <span className="today-hero__number">{focus.id}</span>
        </span>
      )}
      <div className="today-hero__header">
        <h2 id="today-hero-title" className="today-hero__heading">
          {today || !focus ? t('problems.today.title') : t('problems.today.upNext')}
          <span className="today-hero__date">{formatDayUtc(daily.todayIso)}</span>
        </h2>
        {today && <ResetCountdown todayIso={daily.todayIso} />}
      </div>

      {focus ? (
        <>
          <Link to={`/problems/${focus.id}`} className="today-hero__title" viewTransition>
            {focus.title}
          </Link>
          <div className="today-hero__meta">
            <DifficultyLabel level={focus.difficulty} className="difficulty-pill" />
            {focus.rate !== null && <span>{t('problems.today.acceptance', { rate: focus.rate })}</span>}
            {focus.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="today-hero__tag">
                {tag}
              </span>
            ))}
          </div>
          <div className="today-hero__footer">
            {solvedToday ? (
              <ButtonLink to={`/problems/${focus.id}`} size="lg" viewTransition>
                {t('problems.today.reviewSolved')}
              </ButtonLink>
            ) : (
              <ButtonLink to={`/problems/${focus.id}`} variant={primary ? 'primary' : 'secondary'} size="lg" viewTransition trail={LuArrowRight}>
                {t('problems.today.solveNow')}
              </ButtonLink>
            )}
            {today ? (
              <StreakNote streak={daily.streak} solvedToday={solvedToday} />
            ) : (
              <p className="today-hero__note today-hero__note--plain">{t('problems.today.upNextHint')}</p>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="today-hero__title today-hero__title--empty">{t('problems.today.allSolved')}</p>
          <p className="today-hero__hint">{t('problems.today.allSolvedHint')}</p>
        </>
      )}

      {continueProblem && (
        <Link to={`/problems/${continueProblem.id}`} className="today-hero__continue" viewTransition>
          <span className="today-hero__continue-label">{t('problems.today.continueTitle')}</span>
          <span className="today-hero__continue-title">{continueProblem.title}</span>
          <LuArrowRight aria-hidden="true" />
        </Link>
      )}
    </section>
  );
}
